// Estados posibles de notificación que vienen desde backend.
// El frontend NO consulta email_eventos.
// La fuente real ya viene resuelta desde GET /paciente/cargarturnos,
// usando emails_enviados.
export const NOTIFICATION_STATUS = {
  SIN_NOTIFICACION: 'SIN_NOTIFICACION',
  PROGRAMADA: 'PROGRAMADA',
  ENVIADA: 'ENVIADA',
  ENTREGADO: 'ENTREGADO',
  REBOTADO: 'REBOTADO',
  SPAM: 'SPAM',
  DEMORADO: 'DEMORADO',
  ERROR: 'ERROR',
  REINTENTO_PENDIENTE: 'REINTENTO_PENDIENTE'
};

// Normaliza estados internos o variantes que pueden venir desde backend/base.
// En UI usamos siempre los estados finales definidos para frontend.
export const normalizeNotificationStatus = (status) => {
  if (!status) {
    return NOTIFICATION_STATUS.SIN_NOTIFICACION;
  }

  if (status === 'PENDIENTE') {
    return NOTIFICATION_STATUS.PROGRAMADA;
  }

  if (status === 'ENVIADO') {
    return NOTIFICATION_STATUS.ENVIADA;
  }

  return status;
};

// Devuelve el estado principal de notificación del turno.
export const getNotificationStatus = (appointment) => {
  return normalizeNotificationStatus(
    appointment?.estado_notificacion ||
    NOTIFICATION_STATUS.SIN_NOTIFICACION
  );
};

// Formatea fecha YYYY-MM-DD a DD/MM/YYYY.
const formatNotificationDate = (dateString) => {
  if (!dateString) {
    return '';
  }

  const [year, month, day] = String(dateString).split('-');

  if (!year || !month || !day) {
    return dateString;
  }

  return `${day}/${month}/${year}`;
};

// Mensajes principales.
// No usamos “AWS” porque el usuario no necesita saber qué proveedor técnico intervino.
export const getNotificationStatusMessage = (appointment) => {
  const status = getNotificationStatus(appointment);

  if (status === NOTIFICATION_STATUS.SIN_NOTIFICACION) {
    return 'Sin notificación por email.';
  }

  if (status === NOTIFICATION_STATUS.PROGRAMADA) {
    if (appointment?.fecha_notificacion) {
      return `Notificación por email programada para el ${formatNotificationDate(appointment.fecha_notificacion)}.`;
    }

    return 'Notificación por email programada.';
  }

  if (status === NOTIFICATION_STATUS.ENVIADA) {
    return 'Email enviado. Esperando confirmación de entrega.';
  }

  if (status === NOTIFICATION_STATUS.ENTREGADO) {
    return 'Email entregado al servidor de destino.';
  }

  if (status === NOTIFICATION_STATUS.REBOTADO) {
    return 'El email no pudo entregarse.';
  }

  if (status === NOTIFICATION_STATUS.SPAM) {
    return 'El email fue marcado como spam.';
  }

  if (status === NOTIFICATION_STATUS.DEMORADO) {
    return 'La entrega del email está demorada.';
  }

  if (status === NOTIFICATION_STATUS.ERROR) {
    return 'Hubo un error técnico al enviar el email.';
  }

  if (status === NOTIFICATION_STATUS.REINTENTO_PENDIENTE) {
    return 'El sistema intentará enviar este email nuevamente.';
  }

  return 'Estado de email desconocido.';
};

// Motivos técnicos o funcionales del error.
// Los mostramos en lenguaje más entendible.
export const getNotificationErrorMessage = (appointment) => {
  const motivoError = appointment?.motivo_error_notificacion;

  if (!motivoError) {
    return '';
  }

  if (motivoError === 'EMAIL_INVALIDO') {
    return 'La dirección de email parece inválida.';
  }

  if (motivoError === 'EMAIL_RECHAZADO') {
    return 'El servidor de destino rechazó el email.';
  }

  if (motivoError === 'ERROR_INTERNO') {
    return 'Ocurrió un error técnico interno.';
  }

  if (motivoError === 'SIN_TIEMPO_REINTENTO') {
    return 'Ya no queda tiempo para reenviar esta notificación antes del turno.';
  }

  if (motivoError === 'ERROR_DESCONOCIDO') {
    return 'Ocurrió un error desconocido al enviar el email.';
  }

  return `Motivo: ${motivoError}.`;
};

// Mensaje de ayuda según estado.
// No habilita acciones por sí solo; solo explica qué corresponde hacer.
export const getNotificationActionMessage = (appointment) => {
  const status = getNotificationStatus(appointment);
  const motivoError = appointment?.motivo_error_notificacion;

  if (status === NOTIFICATION_STATUS.SIN_NOTIFICACION) {
    return '';
  }

  if (status === NOTIFICATION_STATUS.PROGRAMADA) {
    return '';
  }

  if (status === NOTIFICATION_STATUS.ENVIADA) {
    return 'Todavía no hay confirmación final de entrega.';
  }

  if (status === NOTIFICATION_STATUS.ENTREGADO) {
    return 'Esto no significa que la persona lo haya abierto o leído.';
  }

  if (status === NOTIFICATION_STATUS.DEMORADO) {
    return 'No es un error final; la entrega puede resolverse sola.';
  }

  if (status === NOTIFICATION_STATUS.REINTENTO_PENDIENTE) {
    return 'El sistema intentará enviar este email nuevamente. No hace falta que hagas nada por ahora.';
  }

  if (status === NOTIFICATION_STATUS.SPAM) {
    return 'El email fue marcado como spam. Contactá a la persona por otro medio antes de volver a enviar notificaciones a esta dirección.';
  }

  if (
    status === NOTIFICATION_STATUS.REBOTADO &&
    (
      motivoError === 'EMAIL_INVALIDO' ||
      motivoError === 'EMAIL_RECHAZADO'
    )
  ) {
    return 'Revisá la dirección de email cargada. Si el turno todavía no comenzó, podés corregirla y volver a poner la notificación en cola.';
  }

  if (status === NOTIFICATION_STATUS.REBOTADO) {
    return 'El email no pudo entregarse. Si el turno todavía no comenzó, podés corregir el email y volver a poner la notificación en cola.';
  }

  if (status === NOTIFICATION_STATUS.ERROR) {
    return 'Hubo un error técnico. Si el turno todavía no comenzó, podés volver a poner la notificación en cola.';
  }

  return '';
};

// Devuelve true si corresponde mostrar un indicador visual.
// SIN_NOTIFICACION no muestra nada.
export const shouldShowNotificationIndicator = (appointment) => {
  if (!appointment) {
    return false;
  }

  const status = getNotificationStatus(appointment);

  return (
    status !== NOTIFICATION_STATUS.SIN_NOTIFICACION ||
    appointment.notificar === 'si' ||
    Boolean(appointment.fecha_notificacion) ||
    Boolean(appointment.email_1) ||
    Boolean(appointment.email_2) ||
    (
      Array.isArray(appointment.emails_notificacion) &&
      appointment.emails_notificacion.length > 0
    )
  );
};

// Define una variante visual para styled-components.
export const getNotificationVisualVariant = (appointment) => {
  const status = getNotificationStatus(appointment);

  if (
    status === NOTIFICATION_STATUS.REBOTADO ||
    status === NOTIFICATION_STATUS.SPAM ||
    status === NOTIFICATION_STATUS.ERROR
  ) {
    return 'danger';
  }

  if (
    status === NOTIFICATION_STATUS.DEMORADO ||
    status === NOTIFICATION_STATUS.REINTENTO_PENDIENTE
  ) {
    return 'warning';
  }

  if (status === NOTIFICATION_STATUS.ENTREGADO) {
    return 'success';
  }

  if (status === NOTIFICATION_STATUS.ENVIADA) {
    return 'info';
  }

  if (status === NOTIFICATION_STATUS.PROGRAMADA) {
    return 'scheduled';
  }

  return 'muted';
};

// El sobre queda como base visual.
// Esto es lo que aparece como “sello” arriba del sobre.
export const getNotificationBadgeContent = (appointment) => {
  const status = getNotificationStatus(appointment);

  if (status === NOTIFICATION_STATUS.PROGRAMADA) {
    return '⏱';
  }

  if (status === NOTIFICATION_STATUS.ENVIADA) {
    return '→';
  }

  if (status === NOTIFICATION_STATUS.ENTREGADO) {
    return '✓';
  }

  if (status === NOTIFICATION_STATUS.REBOTADO) {
    return '!';
  }

  if (status === NOTIFICATION_STATUS.SPAM) {
    return 'SPAM';
  }

  if (status === NOTIFICATION_STATUS.DEMORADO) {
    return '⏳';
  }

  if (status === NOTIFICATION_STATUS.ERROR) {
    return '!';
  }

  if (status === NOTIFICATION_STATUS.REINTENTO_PENDIENTE) {
    return '↻';
  }

  return '';
};

// Texto corto por si después lo usamos en una leyenda.
export const getNotificationLabel = (appointment) => {
  const status = getNotificationStatus(appointment);

  if (status === NOTIFICATION_STATUS.PROGRAMADA) {
    return 'Programada';
  }

  if (status === NOTIFICATION_STATUS.ENVIADA) {
    return 'Enviada';
  }

  if (status === NOTIFICATION_STATUS.ENTREGADO) {
    return 'Entregada';
  }

  if (status === NOTIFICATION_STATUS.REBOTADO) {
    return 'Rebotada';
  }

  if (status === NOTIFICATION_STATUS.SPAM) {
    return 'SPAM';
  }

  if (status === NOTIFICATION_STATUS.DEMORADO) {
    return 'Demorada';
  }

  if (status === NOTIFICATION_STATUS.ERROR) {
    return 'Error';
  }

  if (status === NOTIFICATION_STATUS.REINTENTO_PENDIENTE) {
    return 'Reintento';
  }

  return 'Sin notificación';
};

// Devuelve el email principal para mostrar en tooltip/modal.
export const getNotificationPrimaryEmail = (appointment) => {
  if (appointment?.email_1) {
    return appointment.email_1;
  }

  if (
    Array.isArray(appointment?.emails_notificacion) &&
    appointment.emails_notificacion.length > 0
  ) {
    return (
      appointment.emails_notificacion[0]?.email_destino ||
      appointment.emails_notificacion[0]?.email ||
      ''
    );
  }

  return '';
};

// Arma el tooltip completo.
export const getNotificationTooltip = (appointment) => {
  const statusMessage = getNotificationStatusMessage(appointment);
  const errorMessage = getNotificationErrorMessage(appointment);
  const actionMessage = getNotificationActionMessage(appointment);

  const email = getNotificationPrimaryEmail(appointment);
  const emailMessage = email
    ? `Email: ${email}.`
    : '';

  const intento = appointment?.intento_notificacion;
  const intentoMessage =
    intento !== null && intento !== undefined
      ? `Intentos de envío: ${intento}.`
      : '';

  return [
    statusMessage,
    emailMessage,
    errorMessage,
    actionMessage,
    intentoMessage
  ]
    .filter(Boolean)
    .join(' ');
};

export const isNotificationProblem = (appointment) => {
  const status = getNotificationStatus(appointment);

  return (
    status === NOTIFICATION_STATUS.REBOTADO ||
    status === NOTIFICATION_STATUS.SPAM ||
    status === NOTIFICATION_STATUS.ERROR
  );
};

export const shouldShowNotificationProblemIndicator = (appointment) => {
  return isNotificationProblem(appointment);
};

export const getNotificationProblemBadgeContent = (appointment) => {
  const status = getNotificationStatus(appointment);

  if (status === NOTIFICATION_STATUS.SPAM) {
    return '!';
  }

  if (
    status === NOTIFICATION_STATUS.REBOTADO ||
    status === NOTIFICATION_STATUS.ERROR
  ) {
    return '!';
  }

  return '';
};

export const getNotificationProblemTooltip = (appointment) => {
  return getNotificationTooltip(appointment);
};

// Agrupa estados para la vista Semana.
// En Semana no mostramos todos los detalles para no ensuciar la grilla.
// Si el turno no tiene notificación, no mostramos ícono.
export const getWeekNotificationGroup = (appointment) => {
  const status = getNotificationStatus(appointment);

  if (status === NOTIFICATION_STATUS.SIN_NOTIFICACION) {
    return 'none';
  }

  if (
    status === NOTIFICATION_STATUS.REBOTADO ||
    status === NOTIFICATION_STATUS.ERROR
  ) {
    return 'problem';
  }

  if (status === NOTIFICATION_STATUS.SPAM) {
    return 'spam';
  }

  if (
    status === NOTIFICATION_STATUS.PROGRAMADA ||
    status === NOTIFICATION_STATUS.DEMORADO ||
    status === NOTIFICATION_STATUS.REINTENTO_PENDIENTE
  ) {
    return 'process';
  }

  if (
    status === NOTIFICATION_STATUS.ENVIADA ||
    status === NOTIFICATION_STATUS.ENTREGADO
  ) {
    return 'ok';
  }

  return 'none';
};

export const shouldShowWeekNotificationIndicator = (appointment) => {
  return getWeekNotificationGroup(appointment) !== 'none';
};

export const getWeekNotificationVisualVariant = (appointment) => {
  return getWeekNotificationGroup(appointment);
};

export const getWeekNotificationBadgeText = (appointment) => {
  const group = getWeekNotificationGroup(appointment);

  if (group === 'process') {
    return '✉⏱';
  }

  if (group === 'ok') {
    return '✉✓';
  }

  if (group === 'problem') {
    return '✉!';
  }

  if (group === 'spam') {
    return 'SPAM';
  }

  return '';
};

export const getWeekNotificationTooltip = (appointment) => {
  return getNotificationTooltip(appointment);
};

// Agrupa estados de email para la vista Semana.
// En Semana usamos color del bloque, no badges, para evitar ensuciar la grilla.
export const getWeekNotificationColorGroup = (appointment) => {
  const status = getNotificationStatus(appointment);

  if (status === NOTIFICATION_STATUS.SIN_NOTIFICACION) {
    return 'missing';
  }

  if (
    status === NOTIFICATION_STATUS.REBOTADO ||
    status === NOTIFICATION_STATUS.SPAM ||
    status === NOTIFICATION_STATUS.ERROR
  ) {
    return 'danger';
  }

  if (
    status === NOTIFICATION_STATUS.DEMORADO ||
    status === NOTIFICATION_STATUS.REINTENTO_PENDIENTE
  ) {
    return 'warning';
  }

  if (status === NOTIFICATION_STATUS.ENTREGADO) {
    return 'success';
  }

  // PROGRAMADA y ENVIADA quedan con el color normal del turno.
  // La notificación existe, pero no requiere una alerta visual fuerte.
  if (
    status === NOTIFICATION_STATUS.PROGRAMADA ||
    status === NOTIFICATION_STATUS.ENVIADA
  ) {
    return 'default';
  }

  return 'default';
};

// Color principal del bloque semanal según estado de email.
// El estado del turno queda representado por el símbolo/dropdown de la derecha.
export const getWeekNotificationEventColor = (appointment) => {
  const group = getWeekNotificationColorGroup(appointment);

  if (group === 'missing') {
    return '#7E95A8'; // gris azulado: no tiene notificación por email
  }

  if (group === 'danger') {
    return '#DC2626'; // rojo: error/rebote/spam
  }

  if (group === 'warning') {
    return '#D97706'; // amarillo oscuro/naranja: demora/reintento
  }

  if (group === 'success') {
    return '#16A34A'; // verde: entregado
  }

  return '#23AAAF'; // color normal del calendario
};

// Texto para tooltip del bloque en vista Semana.
export const getWeekNotificationColorTooltip = (appointment) => {
  const group = getWeekNotificationColorGroup(appointment);

  if (group === 'missing') {
    return 'Este turno no tiene notificación por email programada.';
  }

  return getNotificationTooltip(appointment);
};

// Estados en los que el usuario puede corregir/reintentar manualmente.
// SPAM queda afuera: no conviene reenviar directo a una dirección que marcó spam.
export const canManuallyRequeueNotification = (appointment) => {
  const status = getNotificationStatus(appointment);

  return (
    status === NOTIFICATION_STATUS.REBOTADO ||
    status === NOTIFICATION_STATUS.ERROR
  );
};

// SPAM requiere contacto por otro medio, no reenvío directo.
export const isSpamNotification = (appointment) => {
  return getNotificationStatus(appointment) === NOTIFICATION_STATUS.SPAM;
};

// Estados donde NO hay que permitir intervención manual por ahora.
export const isAutomaticOrWaitingNotification = (appointment) => {
  const status = getNotificationStatus(appointment);

  return (
    status === NOTIFICATION_STATUS.DEMORADO ||
    status === NOTIFICATION_STATUS.REINTENTO_PENDIENTE ||
    status === NOTIFICATION_STATUS.ENVIADA ||
    status === NOTIFICATION_STATUS.ENTREGADO
  );
};

// Texto del botón de acción manual.
export const getManualRequeueButtonText = (appointment) => {
  const status = getNotificationStatus(appointment);

  if (status === NOTIFICATION_STATUS.REBOTADO) {
    return 'CORREGIR Y REENVIAR EMAIL';
  }

  if (status === NOTIFICATION_STATUS.ERROR) {
    return 'REENVIAR EMAIL';
  }

  return 'REENVIAR EMAIL';
};